import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  ChevronRightIcon, 
  FolderIcon, 
  DocumentIcon, 
  EllipsisVerticalIcon, 
  KeyIcon,
  PencilSquareIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import DocumentNodePermissionsModal from './modals/DocumentNodePermissionsModal';
import { MoveDocumentModal } from './modals/MoveDocumentModal';
import { ShareDocumentModal } from './modals/ShareDocumentModal';
import { documentService } from '../../services/documentService';
import documentDownloadService from '../../services/documentDownloadService';
import { useToast } from '../UI/ToastProvider';
import { getFileTypeIcon } from './fileTypeIcons';
import { DocumentDto, DocumentNode } from '../../types/documents';
import { useDocumentPermissions, DocumentPermissions } from '../../hooks/useDocumentPermissions';
import { ActionBlockedModal } from '../UI/ActionBlockedModal';
import { normalizeDocumentStatus } from '../../utils/workflowConstants';

const DND_NODE_ID_MIME = 'application/x-knowindoc-document-node-id';

function collectDescendantIds(root: DocumentNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: DocumentNode) => {
    ids.add(n.id);
    (n.children ?? []).forEach(walk);
  };
  walk(root);
  return ids;
}

function findNodeById(root: DocumentNode | null, id: string): DocumentNode | null {
  if (!root) return null;
  if (root.id === id) return root;
  for (const c of root.children ?? []) {
    const f = findNodeById(c, id);
    if (f) return f;
  }
  return null;
}

function isDropTargetValidForMove(dragging: DocumentNode, targetFolder: DocumentNode): boolean {
  if (targetFolder.nodeType !== 'folder') return false;
  if (targetFolder.id === dragging.id) return false;
  const forbidden = collectDescendantIds(dragging);
  if (forbidden.has(targetFolder.id)) return false;
  return true;
}

export type DocumentTreeExpandSignal = { version: number; folderIds: string[] };

interface DocumentTreeProps {
  treeData: DocumentNode | null;
  onNodeSelect: (node: DocumentNode) => void;
  selectedNode: DocumentNode | null;
  onExpandNode?: (node: DocumentNode) => void | Promise<void>;
  onDeleteNode?: (node: DocumentNode) => void;
  onEditDocument?: (node: DocumentNode) => void;
  onNodeRenamed?: (updated: DocumentDto) => void;
  onNodeMoved?: () => void;
  onDuplicateDocument?: (nodeId: string) => Promise<void>;
  treeExpandSignal?: DocumentTreeExpandSignal;
}

export const DocumentTree: React.FC<DocumentTreeProps> = ({
  treeData,
  onNodeSelect,
  selectedNode,
  onExpandNode,
  onDeleteNode,
  onEditDocument,
  onNodeRenamed,
  onNodeMoved,
  onDuplicateDocument,
  treeExpandSignal,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set()); // Estados de loading por nó
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: DocumentNode } | null>(null);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedNodeForPermissions, setSelectedNodeForPermissions] = useState<DocumentNode | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<DocumentNode[]>([]);
  const [filteredTreeData, setFilteredTreeData] = useState<DocumentNode | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const skipCommitBlurRef = useRef(false);
  const { addToast } = useToast();

  const [renamingNodeId, setRenamingNodeId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameBaseline, setRenameBaseline] = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [nodeToMove, setNodeToMove] = useState<DocumentNode | null>(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [nodeToShare, setNodeToShare] = useState<DocumentNode | null>(null);
  const [shareModalCanEdit, setShareModalCanEdit] = useState(false);
  const [downloadingNodeId, setDownloadingNodeId] = useState<string | null>(null);

  // Estados para modal de ação bloqueada
  const [showActionBlockedModal, setShowActionBlockedModal] = useState(false);
  const [blockedActionName, setBlockedActionName] = useState<string>('');

  const permissionTargetId = contextMenu?.node?.id ?? selectedNode?.id ?? null;
  const { permissions, isLoading: contextPermissionsLoading } =
    useDocumentPermissions(permissionTargetId);

  const [dndPrepNodeId, setDndPrepNodeId] = useState<string | null>(null);
  const dndSecondaryPermId =
    dndPrepNodeId && dndPrepNodeId !== permissionTargetId ? dndPrepNodeId : null;
  const { permissions: dndPrepPermissions, isLoading: dndPrepLoading } =
    useDocumentPermissions(dndSecondaryPermId);

  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dropHoverTargetId, setDropHoverTargetId] = useState<string | null>(null);
  const [dropHoverValid, setDropHoverValid] = useState<boolean | null>(null);
  const [moveDropSubmitting, setMoveDropSubmitting] = useState(false);
  const draggingActiveRef = useRef(false);

  // Função para verificar se é um documento eletrônico
  const isElectronicDocument = (node: DocumentNode): boolean => {
    return node.nodeType === 'edoc';
  };

  const canRenameNode = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root') return false;
    if (isElectronicDocument(node)) return permissions.canEdit;
    if (node.nodeType === 'folder') return permissions.canEdit || permissions.canCreate;
    return permissions.canEdit;
  };

  const canMoveNode = (node: DocumentNode | null | undefined, perms?: DocumentPermissions): boolean => {
    const effective = perms ?? permissions;
    if (!node?.id || node.id === 'root') return false;
    const p = (node.path ?? '').trim();
    if (p === '/' || p === '') return false;
    if (isElectronicDocument(node)) return effective.canEdit;
    if (node.nodeType === 'folder') return effective.canEdit || effective.canCreate;
    return effective.canEdit;
  };

  const canShareDocument = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root') return false;
    if (node.nodeType === 'folder') return false;
    if (normalizeDocumentStatus(node.status) !== 'published') return false;
    return permissions.canView;
  };

  const canDownloadNode = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root') return false;
    if (node.nodeType === 'folder') return false;
    return permissions.canView;
  };

  const canDuplicateNode = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root') return false;
    return isElectronicDocument(node) && permissions.canEdit;
  };

  const finishRenameIdle = () => {
    setRenamingNodeId(null);
    setRenameValue('');
    setRenameBaseline('');
  };

  // Função para buscar documentos recursivamente na árvore
  const searchInTree = (node: DocumentNode, term: string): DocumentNode[] => {
    const results: DocumentNode[] = [];
    
    // Verifica se o nó atual corresponde ao termo de busca
    if (node.title.toLowerCase().includes(term.toLowerCase())) {
      results.push(node);
    }
    
    // Busca recursivamente nos filhos
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        results.push(...searchInTree(child, term));
      });
    }
    
    return results;
  };

  // Função para filtrar a árvore baseado no termo de busca
  const filterTree = (node: DocumentNode, term: string): DocumentNode | null => {
    if (!term.trim()) {
      return node;
    }

    const termLower = term.toLowerCase();
    
    // Se o nó atual corresponde ao termo, retorna ele
    if (node.title.toLowerCase().includes(termLower)) {
      return node;
    }
    
    // Filtra os filhos
    const filteredChildren = node.children
      .map(child => filterTree(child, term))
      .filter(Boolean) as DocumentNode[];
    
    // Se há filhos filtrados, retorna o nó com os filhos filtrados
    if (filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren
      };
    }
    
    return null;
  };

  // Função para lidar com mudanças no campo de busca
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (!treeData) return;
    
    if (value.trim()) {
      // Busca todos os resultados
      const results = searchInTree(treeData, value);
      setSearchResults(results);
      setShowSearchResults(true);
      
      // Filtra a árvore
      const filtered = filterTree(treeData, value);
      setFilteredTreeData(filtered);
    } else {
      // Limpa a busca
      setSearchResults([]);
      setShowSearchResults(false);
      setFilteredTreeData(treeData);
    }
  };

  // Função para selecionar um resultado da busca
  const handleSearchResultSelect = (node: DocumentNode) => {
    onNodeSelect(node);
    setSearchTerm('');
    setShowSearchResults(false);
    setFilteredTreeData(treeData);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Função para limpar a busca
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    setFilteredTreeData(treeData);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Removido o carregamento inicial, pois treeData vem da prop

  // Inicializar filteredTreeData quando treeData mudar
  useEffect(() => {
    setFilteredTreeData(treeData);
  }, [treeData]);

  // Expande um nó de pasta e carrega os filhos se necessário
  const toggleNode = async (nodeId: string, node: DocumentNode) => {
    const isCurrentlyExpanded = expandedNodes.has(nodeId);
    
    // Se está expandindo e precisa carregar filhos
    if (!isCurrentlyExpanded && node.nodeType === 'folder' && node.countChild > 0) {
      // Adiciona o nó ao estado de loading
      setLoadingNodes(prev => new Set(prev).add(nodeId));
      
      // Expande o nó
      setExpandedNodes(prev => new Set(prev).add(nodeId));
      
      // Chama a função de expansão (que deve carregar os filhos)
      if (onExpandNode) {
        try {
          await onExpandNode(node);
        } catch (error) {
          console.error('Erro ao expandir nó:', error);
          // Se der erro, remove da expansão
          setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(nodeId);
            return newSet;
          });
        } finally {
          // Remove do estado de loading
          setLoadingNodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(nodeId);
            return newSet;
          });
        }
      } else {
        // Se não há função de expansão, remove do loading
        setLoadingNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(nodeId);
          return newSet;
        });
      }
    } else {
      // Se está colapsando ou não precisa carregar filhos
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        if (newSet.has(nodeId)) {
          newSet.delete(nodeId);
        } else {
          newSet.add(nodeId);
        }
        return newSet;
      });
    }
  };

  // Quando um nó é clicado
  const handleNodeClick = async (node: DocumentNode) => { 
    onNodeSelect(node);
    
    // Removido: expansão automática da pasta ao clicar
    // Agora apenas a seta controla a expansão/colapso
  };

  // Novo: duplo clique para expandir/colapsar ou abrir documento
  const handleNodeDoubleClick = async (node: DocumentNode) => {
    // Removido: expansão automática da pasta no duplo clique
    // Agora apenas a seta controla a expansão/colapso
    
    // Para todos os tipos de nó, apenas seleciona
    onNodeSelect(node);
  };

  const handleContextMenu = (e: React.MouseEvent, node: DocumentNode) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, node });
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (contextMenu && treeRef.current && !treeRef.current.contains(e.target as Node)) {
      setContextMenu(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  useEffect(() => {
    if (!treeExpandSignal?.folderIds?.length) return;
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      treeExpandSignal.folderIds.forEach((id) => next.add(id));
      return next;
    });
  }, [treeExpandSignal?.version]);

  const handleContextMenuAction = (action: string) => {
    if (!contextMenu) return;

    switch (action) {
      case 'edit':
        if (onEditDocument) onEditDocument(contextMenu.node);
        break;
      case 'delete':
        if (onDeleteNode) onDeleteNode(contextMenu.node);
        break;
      case 'permissions':
        setSelectedNodeForPermissions(contextMenu.node);
        setPermissionsModalOpen(true);
        break;
      case 'rename': {
        const n = contextMenu.node;
        if (canRenameNode(n)) {
          setRenamingNodeId(n.id);
          setRenameValue(n.title);
          setRenameBaseline(n.title);
        }
        break;
      }
      case 'move': {
        const n = contextMenu.node;
        if (canMoveNode(n)) {
          setNodeToMove(n);
          setMoveModalOpen(true);
        }
        break;
      }
      case 'share': {
        const n = contextMenu.node;
        if (canShareDocument(n)) {
          setShareModalCanEdit(permissions.canEdit);
          setNodeToShare(n);
          setShareModalOpen(true);
        }
        break;
      }
      case 'duplicate': {
        const n = contextMenu.node;
        if (canDuplicateNode(n) && onDuplicateDocument) {
          void onDuplicateDocument(n.id);
        }
        break;
      }
      case 'download': {
        const n = contextMenu.node;
        if (!canDownloadNode(n)) break;
        void (async () => {
          setDownloadingNodeId(n.id);
          try {
            await documentDownloadService.downloadAndSaveDocument(
              n.id,
              n.metadata?.fileName || n.title
            );
            addToast('Documento baixado com sucesso!', 'success');
          } catch (error) {
            let errorMessage = 'Erro ao baixar documento';
            if (error instanceof Error) {
              if (error.message.includes('401')) {
                errorMessage = 'Não autorizado. Faça login novamente.';
              } else if (error.message.includes('404')) {
                errorMessage = 'Documento não encontrado.';
              } else if (error.message.includes('403')) {
                errorMessage = 'Acesso negado a este documento.';
              } else {
                errorMessage = error.message;
              }
            }
            addToast(errorMessage, 'error');
          } finally {
            setDownloadingNodeId(null);
          }
        })();
        break;
      }
    }

    setContextMenu(null);
  };

  const handleCancelRename = () => {
    skipCommitBlurRef.current = true;
    finishRenameIdle();
  };

  const handleCommitRename = async () => {
    if (!renamingNodeId || renameSubmitting) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      addToast('O nome não pode ser vazio.', 'error');
      return;
    }
    if (trimmed === renameBaseline.trim()) {
      finishRenameIdle();
      return;
    }
    setRenameSubmitting(true);
    try {
      const dto = await documentService.rename(renamingNodeId, trimmed);
      onNodeRenamed?.(dto);
      finishRenameIdle();
    } catch (error: unknown) {
      const ax = error as { response?: { status?: number; data?: { message?: string } } };
      const status = ax.response?.status;
      const msg = ax.response?.data?.message;
      if (status === 400 || status === 409) {
        addToast(
          msg || (status === 409 ? 'Já existe um item com este nome no mesmo diretório.' : 'Nome inválido.'),
          'error'
        );
      } else if (msg) {
        addToast(msg, 'error');
      } else {
        addToast('Não foi possível renomear o item.', 'error');
      }
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleRenameBlur = () => {
    setTimeout(() => {
      if (skipCommitBlurRef.current) {
        skipCommitBlurRef.current = false;
        return;
      }
      void handleCommitRename();
    }, 0);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleCommitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  };

  useEffect(() => {
    if (renamingNodeId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingNodeId]);

  // Funções para mostrar modal de ação bloqueada
  const showActionBlocked = (actionName: string) => {
    setBlockedActionName(actionName);
    setShowActionBlockedModal(true);
  };

  const treeRootForDnd = filteredTreeData ?? treeData;
  const draggingNodeResolved = useMemo(() => {
    if (!draggingNodeId || !treeRootForDnd) return null;
    return findNodeById(treeRootForDnd, draggingNodeId);
  }, [draggingNodeId, treeRootForDnd]);

  const executeMoveDrop = useCallback(
    async (sourceId: string, destinationFolderId: string) => {
      setMoveDropSubmitting(true);
      try {
        await documentService.moveDocument(sourceId, destinationFolderId);
        addToast('Item movido com sucesso.', 'success');
        onNodeMoved?.();
      } catch (error: unknown) {
        const ax = error as { response?: { status?: number; data?: { message?: string } } };
        const status = ax.response?.status;
        const msg = ax.response?.data?.message;
        if (status === 400) {
          addToast(msg || 'Destino inválido ou operação não permitida.', 'error');
        } else if (status === 403) {
          addToast(msg || 'Acesso negado.', 'error');
        } else if (status === 404) {
          addToast(msg || 'Documento ou destino não encontrado.', 'error');
        } else if (status === 409) {
          addToast(msg || 'Já existe um item com este nome no mesmo diretório.', 'error');
        } else if (msg) {
          addToast(msg, 'error');
        } else {
          addToast('Não foi possível mover o item.', 'error');
        }
      } finally {
        setMoveDropSubmitting(false);
      }
    },
    [addToast, onNodeMoved]
  );

  useEffect(() => {
    const up = () => {
      if (!draggingActiveRef.current) {
        setDndPrepNodeId(null);
      }
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const canMovePathOnly = (node: DocumentNode | null | undefined) => {
    if (!node?.id || node.id === 'root') return false;
    const p = (node.path ?? '').trim();
    return p !== '/' && p !== '';
  };

  const getMovePermissionsForNode = (n: DocumentNode): {
    perms: DocumentPermissions | null;
    loading: boolean;
  } => {
    if (n.id === permissionTargetId) {
      return { perms: permissions, loading: contextPermissionsLoading };
    }
    if (n.id === dndSecondaryPermId) {
      return { perms: dndPrepPermissions, loading: dndPrepLoading };
    }
    return { perms: null, loading: false };
  };

  const renderNode = (node: DocumentNode, level: number = 0, treeRoot: DocumentNode | null = treeRootForDnd) => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode?.id === node.id;
    const isLoading = loadingNodes.has(node.id);

    const showChevron = node.nodeType === 'folder' && node.countChild > 0;

    const { perms: movePerms, loading: movePermsLoading } = getMovePermissionsForNode(node);
    const prepMatchesThisRow =
      node.id === permissionTargetId || node.id === dndPrepNodeId;
    const draggable =
      canMovePathOnly(node) &&
      prepMatchesThisRow &&
      !movePermsLoading &&
      movePerms !== null &&
      canMoveNode(node, movePerms);

    const isDropHover = dropHoverTargetId === node.id;
    const dropValid =
      isDropHover &&
      dropHoverValid === true &&
      node.nodeType === 'folder' &&
      draggingNodeResolved !== null;
    const dropInvalid = isDropHover && dropHoverValid === false;
    const isDraggingSource = draggingNodeId === node.id;

    const rowClass = [
      'flex items-center py-1 px-2 hover:bg-gray-100 group rounded-sm touch-manipulation',
      isSelected ? 'bg-blue-50' : '',
      isDraggingSource ? 'opacity-60' : '',
      dropValid ? 'ring-1 ring-emerald-600/25 ring-inset bg-emerald-500/[0.07]' : '',
      dropInvalid ? 'ring-1 ring-rose-500/25 ring-inset bg-rose-500/[0.06]' : '',
      draggable ? 'cursor-grab active:cursor-grabbing' : '',
      !draggable && !isDropHover ? 'cursor-pointer' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div key={node.id} className="select-none">
        <div
          className={rowClass}
          draggable={draggable}
          style={{ paddingLeft: `${8 + (level * 16)}px` }}
          onPointerEnter={() => {
            if (!canMovePathOnly(node)) return;
            setDndPrepNodeId(node.id);
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            const t = e.target as HTMLElement;
            if (t.closest('button') || t.closest('input') || t.closest('.tree-node-chevron')) return;
            if (renamingNodeId === node.id) return;
            if (!canMovePathOnly(node)) return;
            setDndPrepNodeId(node.id);
          }}
          onDragStart={(e) => {
            const { perms, loading } = getMovePermissionsForNode(node);
            if (loading || !perms || !canMoveNode(node, perms)) {
              e.preventDefault();
              return;
            }
            e.stopPropagation();
            draggingActiveRef.current = true;
            setDraggingNodeId(node.id);
            e.dataTransfer.setData(DND_NODE_ID_MIME, node.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={() => {
            draggingActiveRef.current = false;
            setDraggingNodeId(null);
            setDndPrepNodeId(null);
            setDropHoverTargetId(null);
            setDropHoverValid(null);
          }}
          onDragOver={(e) => {
            if (!draggingNodeResolved || !treeRoot) return;
            e.preventDefault();
            e.stopPropagation();
            if (node.nodeType === 'folder') {
              const valid = isDropTargetValidForMove(draggingNodeResolved, node);
              e.dataTransfer.dropEffect = valid ? 'move' : 'none';
              setDropHoverTargetId(node.id);
              setDropHoverValid(valid);
            } else {
              e.dataTransfer.dropEffect = 'none';
              setDropHoverTargetId(node.id);
              setDropHoverValid(false);
            }
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDropHoverTargetId(null);
              setDropHoverValid(null);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (node.nodeType !== 'folder' || !treeRoot) return;
            const sourceId = e.dataTransfer.getData(DND_NODE_ID_MIME);
            const source = findNodeById(treeRoot, sourceId);
            if (!source || !isDropTargetValidForMove(source, node)) return;
            void executeMoveDrop(source.id, node.id);
          }}
          onClick={() => handleNodeClick(node)}
          onDoubleClick={() => handleNodeDoubleClick(node)}
          onContextMenu={(e) => handleContextMenu(e, node)}
        >
          {showChevron && (
            <div className="mr-1 shrink-0 tree-node-chevron cursor-pointer">
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              ) : (
                <ChevronRightIcon
                  className={`h-4 w-4 transition-transform cursor-pointer ${
                    isExpanded ? 'transform rotate-90' : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    toggleNode(node.id, node);
                  }}
                />
              )}
            </div>
          )}
          {!showChevron && isLoading && (
            <div className="mr-1">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
          {node.nodeType === 'folder' ? (
            <FolderIcon className="h-5 w-5 mr-1 text-yellow-500 pointer-events-none shrink-0" aria-hidden />
          ) : node.nodeType === 'edoc' ? (
            <DocumentIcon className="h-5 w-5 mr-1 text-blue-500 pointer-events-none shrink-0" aria-hidden />
          ) : (
            (() => {
              const fileName = node.metadata?.fileName || node.title || '';
              const ext = fileName.split('.').pop()?.toLowerCase() || '';
              return (
                <span className="mr-1 pointer-events-none shrink-0">{getFileTypeIcon(ext)}</span>
              );
            })()
          )}
          {renamingNodeId === node.id ? (
            <input
              ref={renameInputRef}
              className="text-sm flex-1 min-w-0 border border-blue-500 rounded px-1 py-0.5"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              disabled={renameSubmitting}
            />
          ) : (
            <span className="text-sm flex-1 min-w-0 pointer-events-none select-none">{node.title}</span>
          )}
          {renamingNodeId !== node.id && (
            <button
              type="button"
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e, node);
              }}
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {isExpanded && node.children.length > 0 && (
          <div className="ml-4">
            {node.children.map((child) => renderNode(child, level + 1, treeRoot))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={treeRef} className="h-full flex flex-col">
      <div className="p-4 border-b">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar documentos..."
            className="w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={handleSearchChange}
            ref={searchInputRef}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto p-4 relative"
        onDragOver={(e) => {
          if (!draggingNodeResolved) return;
          e.preventDefault();
        }}
      >
        {moveDropSubmitting && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 pointer-events-auto">
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2 shadow border border-gray-200 text-sm text-gray-700">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              Movendo…
            </div>
          </div>
        )}
        {loading && <div>Carregando...</div>}
        {showSearchResults && searchResults.length > 0 ? (
          <div>
            <div className="mb-2 text-sm text-gray-500">
              {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
            </div>
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="py-2 px-3 hover:bg-gray-100 cursor-pointer rounded-md mb-1"
                onClick={() => handleSearchResultSelect(result)}
              >
                <div className="flex items-center">
                  {result.nodeType === 'folder' ? (
                    <FolderIcon className="h-4 w-4 mr-2 text-yellow-500" />
                  ) : (
                    <DocumentIcon className="h-4 w-4 mr-2 text-blue-500" />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{result.title}</div>
                    {result.path && result.path !== '/' && (
                      <div className="text-xs text-gray-500">
                        {result.path.length > 40 ? `${result.path.substring(0, 37)}...` : result.path}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          filteredTreeData && renderNode(filteredTreeData, 0, filteredTreeData)
        )}
      </div>

      {contextMenu && (
        <div
          className="fixed bg-white shadow-lg rounded-lg py-2 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {(contextMenu.node && isElectronicDocument(contextMenu.node) && permissions.canEdit) ||
           (contextMenu.node && canRenameNode(contextMenu.node)) ||
           (contextMenu.node && canMoveNode(contextMenu.node)) ||
           (contextMenu.node && canDuplicateNode(contextMenu.node)) ||
           (contextMenu.node && canShareDocument(contextMenu.node)) ||
           (contextMenu.node && canDownloadNode(contextMenu.node)) ||
           permissions.canManagePermissions ||
           permissions.canDelete ? (
            <div className="px-3 py-1 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500">Ações</span>
            </div>
          ) : null}

          {contextMenu.node && isElectronicDocument(contextMenu.node) && permissions.canEdit && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('edit')}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Editar
            </button>
          )}

          {contextMenu.node && canRenameNode(contextMenu.node) && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('rename')}
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Renomear
            </button>
          )}

          {contextMenu.node && canMoveNode(contextMenu.node) && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('move')}
            >
              <ArrowsRightLeftIcon className="h-4 w-4 mr-2" />
              Mover
            </button>
          )}

          {contextMenu.node && canDuplicateNode(contextMenu.node) && onDuplicateDocument && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('duplicate')}
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
              Duplicar
            </button>
          )}

          {contextMenu.node && canShareDocument(contextMenu.node) && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('share')}
            >
              <ShareIcon className="h-4 w-4 mr-2" />
              Compartilhar
            </button>
          )}

          {contextMenu.node && canDownloadNode(contextMenu.node) && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center disabled:opacity-50"
              disabled={downloadingNodeId === contextMenu.node.id}
              onClick={() => handleContextMenuAction('download')}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {downloadingNodeId === contextMenu.node.id ? 'Baixando...' : 'Baixar'}
            </button>
          )}

          {/* Botão de Permissões - apenas se tiver permissão para gerenciar permissões */}
          {permissions.canManagePermissions && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center"
              onClick={() => handleContextMenuAction('permissions')}
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              Permissões
            </button>
          )}

          {permissions.canDelete && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-red-600 text-sm flex items-center"
              onClick={() => handleContextMenuAction('delete')}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Excluir
            </button>
          )}
        </div>
      )}

      <MoveDocumentModal
        isOpen={moveModalOpen}
        onClose={() => {
          setMoveModalOpen(false);
          setNodeToMove(null);
        }}
        treeData={treeData}
        nodeToMove={nodeToMove}
        onExpandNode={onExpandNode}
        onMoved={onNodeMoved}
      />

      <ShareDocumentModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setNodeToShare(null);
          setShareModalCanEdit(false);
        }}
        documentId={nodeToShare?.id ?? null}
        documentTitle={nodeToShare?.title ?? ''}
        canEditSharing={shareModalCanEdit}
      />

      {selectedNodeForPermissions && (
        <DocumentNodePermissionsModal
          isOpen={permissionsModalOpen}
          onClose={() => {
            setPermissionsModalOpen(false);
            setSelectedNodeForPermissions(null);
          }}
          nodeId={selectedNodeForPermissions.id}
          nodeName={selectedNodeForPermissions.title}
          nodePath={selectedNodeForPermissions.path}
        />
      )}

      {showActionBlockedModal && (
        <ActionBlockedModal
          isOpen={showActionBlockedModal}
          actionName={blockedActionName}
          onClose={() => setShowActionBlockedModal(false)}
        />
      )}
    </div>
  );
}; 